import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import { Block, BlockBetween, BlockHead, BlockHeadContent, BlockTitle, Icon } from "../../components/Component";
import useLogout from "../../hooks/useLogout";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import ApiPaginationDataTable from "../../MyComp/UI/DataTable/ApiPaginationDataTable";
import { hospitalDoctorList } from "../../api/Hospital";
import { UserAvatarCell } from "../../MyComp/ImageView/UserAvatarCell";
import { BASE_URL_IMG } from "../../assets/constants";

const User = () => {
  const {
    register,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      server_search: "",
    },
  });

  const { logout } = useLogout();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageOpen, setImageOpen] = useState(false);
  const [mode, setMode] = useState("Add");
  const [isOpen, setIsOpen] = useState(false);

  // pagination
  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setValue("page", page);
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
    setValue("page", page);
  };
  const handleImageOpenPreview = (row, name) => {
    setImageOpen(!imageOpen);
    setValue("image_preview", row);
    trigger("image_preview");
  };

  const columns = [
    {
      name: "Hospital Name",
      selector: (row) => row?.hospital_name || row?.hospitalName,
      cell: (row) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "6px 0",
          }}
        >
          <UserAvatarCell
            row={row}
            image={row?.photo ? `${BASE_URL_IMG}${row.photo}` : ""}
            name={row?.hospital_name || row?.hospitalName}
            handleImageOpenPreview={handleImageOpenPreview}
          />

          <div style={{ display: "flex", flexDirection: "column" }}>
            <span>{row?.hospital_name || row?.hospitalName || "N/A"}</span>
          </div>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Department",
      selector: (row) => row?.department_name || row?.departmentName,
      sortable: true,
    },
    {
      name: "Doctor Name",
      selector: (row) => row?.full_name,
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row?.email,
      sortable: true,
    },
    {
      name: "Phone",
      cell: (row) => (
        <div title={row?.phone}>
          {truncateByCharacters(row?.phone, 12)}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Medical License No",
      cell: (row) => (
        <div title={row?.qualification?.medical_license_number}>
          {truncateByCharacters(row?.qualification?.medical_license_number, 12)}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Experience (Years)",
      selector: (row) => row?.years_of_experience,
      sortable: true,
    },
    {
      name: "Specialization",
      cell: (row) => (
        <div title={row?.specialization_name || row?.specializationName}>
          {truncateByCharacters(row?.specialization_name || row?.specializationName, 20)}
        </div>
      ),
      sortable: true,
    },
    {
      name: "Action",
      wrap: true,
      width: "120px",
      cell: (row) => (
        <div>
          <Button outline color="warning" size="xs" className="me-1" onClick={() => handleEdit(row)}>
            <Icon name="edit" />
          </Button>
          {/* <Button outline color="danger" size="xs" onClick={() => handleDeleteClick(row)}>
            <Icon name="trash" />
          </Button> */}
        </div>
      ),
    },
  ];

  useEffect(() => {
    serverSideSearch();
  }, [perPage, currentPage]);

  const serverSideSearch = () => {
    const body = {
      search: watch("server_search"),
      page: currentPage,
      limit: perPage,
    };

    setLoading(true);

    hospitalDoctorList(body)
      .then((res) => {
        setLoading(false);
        const status = res?.data?.status;

        if (status === true) {
          const responseData = res?.data?.data;
          const doctorList = Array.isArray(responseData)
            ? responseData
            : responseData?.result || [];
          const total = Array.isArray(responseData)
            ? responseData.length
            : responseData?.totalRecords || doctorList.length || 0;

          setTotalRows(total);
          setTableData(doctorList);
        } else if (status === false) {
          setTableData([]);
          setTotalRows(0);
        } else if (status === "expired") {
          logout();
          toast.error(res.data.message);
        }
      })
      .catch((err) => {
        setLoading(false);
        console.log("catch error", err);
      });
  };

  useEffect(() => {
    if (watch("server_search")?.length === 0) {
      serverSideSearch();
    }
  }, [watch("server_search")]);

  const truncateByCharacters = (text = "", charLimit = 20) => {
    if (!text) return "";
    return text.length > charLimit
      ? text.slice(0, charLimit) + "..."
      : text;
  };


  const handleEdit = (row) => {
    console.log(row, "=====================");
    
    navigate("/doctor-form", {
      state: {
        mode: "Edit",
        dataEdit: row, // ✅ Changed from 'data' to 'dataEdit'
        doctorId: row?.id // ✅ Changed from 'id' to 'doctorId'
      },
    });
  };

  return (
    <>
      <Head title="Doctors" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page tag="h3">
                Hospital Doctors
              </BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                outline
                className="me-n1"
                onClick={() => {
                  navigate("/doctor-form", { state: { mode: "Add" } });
                }}
              >
                <Icon name="plus" />
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <ApiPaginationDataTable
            columns={columns}
            data={tableData}
            title={"Hospital Doctor List"}
            isLoading={loading}
            register={register}
            trigger={trigger}
            watch={watch}
            setValue={setValue}
            errors={errors}
            totalRows={totalRows}
            perPage={perPage}
            serverSearch
            paginationServer
            serverSideSearch={serverSideSearch}
            handlePageChange={handlePageChange}
            handlePerRowsChange={handlePerRowsChange}
          />
        </Block>
      </Content>

    </>
  );
};

export default User;
